// src/Pages/Rental/Rental.jsx
import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import AOS from "aos";
import "aos/dist/aos.css";
import RentalDateSelector from "../../components/RentalDateSelector/RentalDateSelector";
import MultiLayerParallax from "../../components/MultiLayerParallax/MultiLayerParallax.tsx";
import RentalHelmetComponent from "./RentalHelmetComponent";
import SoundRental from "../../components/SoundRental/SoundRental.jsx";
import LightRental from "../../components/LightRental/LightRental.jsx";
import OtherRentals from "../../components/OtherRentals/OtherRentals.jsx";
import { useBooking } from "../../context/BookingContext";
import { useLocation } from "react-router-dom";
import { adaptRentalProduct } from "../../utils/rentalProductAdapter";

const pageShellClass =
  "relative w-full overflow-hidden bg-gradient-to-br from-page via-surface2 to-surface3 text-[#111827] dark:from-darkpage dark:via-darksurface dark:to-darksurface2 dark:text-white";

const heroCardClass =
  "relative mx-auto max-w-7xl overflow-hidden rounded-[2rem] border border-[#DDE7E5] bg-white/90 p-6 shadow-xl backdrop-blur-xl dark:border-darkborder dark:bg-[#111114]/95 sm:p-8 lg:p-10";

const badgeClass =
  "mb-5 inline-flex rounded-full border border-primary/40 bg-primary/10 px-4 py-1.5 font-customFont1 text-xs font-extrabold uppercase tracking-[0.14em] !text-[#111827] shadow-sm dark:!text-white";

const titleClass =
  "font-customFont2 font-bold uppercase leading-tight text-[#111827] dark:text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl";

const introTextClass =
  "mt-6 max-w-5xl font-customFont1 text-lg leading-relaxed !text-[#374151] dark:!text-[#D1D5DB] sm:text-xl [&_strong]:font-extrabold [&_strong]:!text-[#111827] dark:[&_strong]:!text-white";

const infoBoxClass =
  "rounded-3xl border border-primary/30 bg-primary/10 p-5 dark:border-primary/30 dark:bg-primary/5";

const getCart = () => JSON.parse(localStorage.getItem("rentalCart")) || [];

const getRentalApiBase = () =>
  process.env.NODE_ENV === "production"
    ? "https://spiderlegsproductions.com"
    : "http://localhost:3111";

const Rental = () => {
  const { selectedDates, setSelectedDates } = useBooking();
  const location = useLocation();

  const [cartItems, setCartItems] = useState(getCart());
  const [selectedQuantities, setSelectedQuantities] = useState({});

  const [allDevices, setAllDevices] = useState([]);
  const [availableDevices, setAvailableDevices] = useState([]);
  const [availability, setAvailability] = useState({});

  // AOS
  useEffect(() => {
    AOS.init({ duration: 800, once: true });
  }, []);

  useEffect(() => {
    if (window.gtag) {
      window.gtag("event", "vuokrattavat_sivu_avattu", {
        sivu: "vuokrattavat",
      });
    }
  }, []);

  useEffect(() => {
    const now = new Date();
    now.setHours(12, 0, 0, 0);

    const todayDateTimeLocal = now.toISOString().slice(0, 16);

    const tomorrow = new Date(now);
    tomorrow.setDate(now.getDate() + 1);
    const tomorrowDateTimeLocal = tomorrow.toISOString().slice(0, 16);

    const savedDates = JSON.parse(localStorage.getItem("modalBookingData"));

    if (savedDates?.startDate) {
      const savedStart = new Date(savedDates.startDate);
      savedStart.setHours(12, 0, 0, 0);

      if (savedStart < now) {
        const updatedDates = {
          startDate: todayDateTimeLocal,
          endDate: tomorrowDateTimeLocal,
        };

        setSelectedDates(updatedDates);
        localStorage.setItem("modalBookingData", JSON.stringify(updatedDates));
      } else {
        setSelectedDates(savedDates);
      }
    } else {
      const initialDates = {
        startDate: todayDateTimeLocal,
        endDate: tomorrowDateTimeLocal,
      };

      setSelectedDates(initialDates);
      localStorage.setItem("modalBookingData", JSON.stringify(initialDates));
    }
  }, [setSelectedDates]);

  // Laitteiden haku
  const fetchRentals = useCallback(async () => {
    try {
      const RENTAL_API_BASE = getRentalApiBase();

      const res = await fetch(`${RENTAL_API_BASE}/api/products`);

      if (!res.ok) {
        throw new Error(`Virhe tuotteiden haussa: ${res.status}`);
      }

      const data = await res.json();

      if (data.ok && Array.isArray(data.items)) {
        const adapted = data.items.map(adaptRentalProduct);
        setAllDevices(adapted);
      } else {
        console.warn("Oma API ei palauttanut items-listaa.");
      }
    } catch (err) {
      console.error("Virhe tuotteiden haussa (oma API):", err);
    }
  }, []);

  useEffect(() => {
    fetchRentals();
  }, [fetchRentals]);

  const fetchAvailability = useCallback(async () => {
    if (!selectedDates?.startDate || !selectedDates?.endDate) return;

    try {
      const API_BASE = getRentalApiBase();

      const start = selectedDates.startDate.slice(0, 10);
      const end = selectedDates.endDate.slice(0, 10);

      const res = await fetch(
        `${API_BASE}/api/availability?start=${start}&end=${end}`
      );

      if (!res.ok) {
        throw new Error("Availability fetch failed");
      }

      const data = await res.json();

      if (data.ok) {
        setAvailability(data.items || {});
      }
    } catch (err) {
      console.error("Availability error:", err);
    }
  }, [selectedDates]);

  const checkAvailability = useCallback(() => {
    if (!allDevices.length) return;

    const updated = allDevices.map((device) => {
      const stock = Number(device.available) || 0;
      const booked = Number(availability[device.id]) || 0;
      const available = Math.max(0, stock - booked);

      return {
        ...device,
        available,
        isBooked: available <= 0,
      };
    });

    setAvailableDevices(updated);
  }, [allDevices, availability]);

  useEffect(() => {
    fetchAvailability();
  }, [fetchAvailability]);

  useEffect(() => {
    checkAvailability();
  }, [checkAvailability]);

  useEffect(() => {
    const savedDates = JSON.parse(localStorage.getItem("modalBookingData"));

    if (savedDates?.startDate && savedDates?.endDate) {
      setSelectedDates(savedDates);
    }
  }, [setSelectedDates]);

  useEffect(() => {
    if (location.hash) {
      const scrollToAnchor = () => {
        const id = location.hash.replace("#", "");
        const element = document.getElementById(id);

        if (element) {
          setTimeout(() => {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 200);
        }
      };

      scrollToAnchor();
    }
  }, [location]);

  useEffect(() => {
    const handleCartUpdate = () => {
      setCartItems(getCart());
    };

    window.addEventListener("cartUpdated", handleCartUpdate);

    return () => {
      window.removeEventListener("cartUpdated", handleCartUpdate);
    };
  }, []);

  const handleRentalDatesChange = (dates) => {
    if (!dates) {
      console.warn("Päivämäärät puuttuvat:", dates);
      return;
    }

    const startDate = dates.startDate || dates.start;
    const endDate = dates.endDate || dates.end || startDate;

    if (!startDate || !endDate) {
      console.warn("Päivämäärät puuttuvat:", dates);
      return;
    }

    const newDates = {
      startDate,
      endDate,
    };

    setSelectedDates(newDates);
    localStorage.setItem("modalBookingData", JSON.stringify(newDates));

    if (window.gtag) {
      window.gtag("event", "vuokra_paivat_valittu", {
        alku: newDates.startDate,
        loppu: newDates.endDate,
      });
    }
  };

  const addToCart = (product, quantityToAdd = 1) => {
    const availableTotal = parseInt(product.available, 10) || 0;

    if (quantityToAdd > availableTotal) {
      toast.error(
        `Tuotetta ${product.name} voi lisätä enintään ${availableTotal} kpl.`
      );
      return;
    }

    const updatedCart = [...cartItems];
    const existingProduct = updatedCart.find((i) => i.id === product.id);

    if (existingProduct) {
      existingProduct.quantity += quantityToAdd;
      existingProduct.totalPrice =
        parseFloat(existingProduct.price) * existingProduct.quantity;
    } else {
      const price = parseFloat(product.price) || 0;

      updatedCart.push({
        id: product.id,
        name: product.name,
        slug: product.slug,
        image: product.image,
        price,
        quantity: quantityToAdd,
        totalPrice: price * quantityToAdd,
      });
    }

    const totalInCart =
      updatedCart.find((i) => i.id === product.id)?.quantity || quantityToAdd;

    localStorage.setItem("rentalCart", JSON.stringify(updatedCart));

    setTimeout(() => {
      setCartItems(updatedCart);
      window.dispatchEvent(new Event("cartUpdated"));
    }, 0);

    const toastId = `toast-${product.id}`;
    const singleItem = availableTotal === 1;
    const allAdded = totalInCart >= availableTotal;

    const message = singleItem
      ? `${product.name} lisätty vuokrauskoriin!`
      : allAdded
        ? `Kaikki ${product.name} ovat nyt vuokrauskorissa!`
        : `${quantityToAdd} x ${product.name} lisätty vuokrauskoriin – nyt yhteensä ${totalInCart} kpl.`;

    if (toast.isActive(toastId)) {
      toast.update(toastId, {
        render: message,
        type: "success",
        autoClose: 3000,
        isLoading: false,
      });
    } else {
      toast.success(message, { toastId });
    }

    if (window.gtag) {
      window.gtag("event", "vuokratuote_lisatty_koriin", {
        tuote_id: product.id,
        tuote_nimi: product.name,
        maara: quantityToAdd,
        hinta: product.price,
      });
    }
  };

  const removeFromCart = (product, quantityToRemove = 1) => {
    try {
      const cart = getCart();
      const existingProduct = cart.find((i) => i.id === product.id);

      if (!existingProduct) {
        toast.error("Tuotetta ei ole korissa.", { position: "top-right" });
        return;
      }

      let updatedCart = [...cart];

      if (existingProduct.quantity > quantityToRemove) {
        existingProduct.quantity -= quantityToRemove;
        existingProduct.totalPrice =
          parseFloat(existingProduct.price) * existingProduct.quantity;

        toast.info(`${quantityToRemove} x ${product.name} poistettu korista.`, {
          position: "bottom-left",
          theme: "colored",
        });
      } else {
        updatedCart = updatedCart.filter((item) => item.id !== product.id);

        toast.info(`${product.name} on poistettu vuokrauskorista.`, {
          position: "bottom-left",
          theme: "colored",
        });
      }

      localStorage.setItem("rentalCart", JSON.stringify(updatedCart));

      if (window.gtag) {
        window.gtag("event", "vuokratuote_poistettu_korista", {
          tuote_id: product.id,
          tuote_nimi: product.name,
        });
      }

      setCartItems(updatedCart);
      window.dispatchEvent(new Event("cartUpdated"));
    } catch (error) {
      toast.error("Virhe poistettaessa tuotetta korista.", {
        position: "top-right",
      });

      console.error(error);
    }
  };

  const incrementQuantity = (productId, available) => {
    setSelectedQuantities((prev) => {
      const current = prev[productId] || 1;

      if (current < available) {
        return { ...prev, [productId]: current + 1 };
      }

      return prev;
    });
  };

  const decrementQuantity = (productId) => {
    setSelectedQuantities((prev) => {
      const current = prev[productId] || 1;

      if (current > 1) {
        return { ...prev, [productId]: current - 1 };
      }

      return prev;
    });
  };

  const soundDevices = availableDevices.filter((dev) => dev.kategoriaID === 1);
  const lightDevices = availableDevices.filter((dev) => dev.kategoriaID === 2);
  const otherDevices = availableDevices.filter(
    (dev) =>
      dev.kategoriaID === 3 &&
      !dev.name?.toLowerCase().includes("hirvi")
  );

  return (
    <>
      <RentalHelmetComponent />

      <main className={pageShellClass}>
        <MultiLayerParallax />

        <div
          aria-hidden="true"
          className="
            pointer-events-none absolute left-1/2 top-16 h-72 w-72
            -translate-x-1/2 rounded-full bg-primary/15 blur-3xl
            dark:bg-primary/10
          "
        />

        <div
          aria-hidden="true"
          className="
            pointer-events-none absolute -right-24 top-[520px] h-96 w-96
            rounded-full bg-[#30f2cf]/10 blur-3xl
            dark:bg-primary/10
          "
        />

        <div className="relative mx-auto px-4 pb-24 pt-24 sm:px-6 lg:px-8">
          <section className={heroCardClass}>
            <div
              aria-hidden="true"
              className="
                pointer-events-none absolute -right-20 -top-20 h-56 w-56
                rounded-full bg-primary/10 blur-3xl
                dark:bg-primary/10
              "
            />

            <div className="relative grid grid-cols-1 gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-end">
              <div>
                <span className={badgeClass}>Vuokrattavat</span>

                <h1 className={titleClass}>
                  Tapahtumatekniikan vuokraus
                </h1>

                <div className={introTextClass}>
                  <p>
                    Valitse{" "}
                    <strong>vuokrauksen aloitus- ja lopetuspäivä</strong>.
                    Tällä sivulla voit varata{" "}
                    <strong>
                      äänentoisto-, valo- ja muita tapahtumatekniikan laitteita
                      itsepalveluna
                    </strong>{" "}
                    suoraan kalenterista.
                  </p>

                  <p className="mt-5">
                    Kaikki laitteet vuokrataan{" "}
                    <strong>vuorokausiperusteisesti</strong>, poikkeuksena{" "}
                    <strong>HIRVI 350 -peräkärry</strong>, joka vuokrataan{" "}
                    <strong>tuntiperusteisesti</strong>.
                  </p>
                </div>
              </div>

              <aside className={infoBoxClass}>
                <p className="font-customFont1 text-sm font-extrabold uppercase tracking-[0.12em] !text-[#111827] dark:!text-white">
                  Näin varaat
                </p>

                <ul className="mt-4 space-y-3 font-customFont1 text-sm font-bold leading-relaxed !text-[#111827] dark:!text-white">
                  <li className="flex gap-3">
                    <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary" />
                    <span>
                      Järjestelmä ehdottaa automaattisesti sopivaa
                      lopetuspäivää.
                    </span>
                  </li>

                  <li className="flex gap-3">
                    <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary" />
                    <span>
                      Näet heti tuotteiden saatavuuden valitulle ajalle.
                    </span>
                  </li>

                  <li className="flex gap-3">
                    <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary" />
                    <span>
                      Peräkärry varataan tuotteen omalta sivulta omalla
                      kalenterilla.
                    </span>
                  </li>
                </ul>
              </aside>
            </div>

            <div
              id="rental-dates"
              className="
                relative mt-9 scroll-mt-24 rounded-3xl border border-[#DDE7E5]
                bg-[#F8FAFC]/80 p-5 shadow-sm
                dark:border-darkborder dark:bg-darksurface/80
              "
            >
              <RentalDateSelector
                selectedStart={selectedDates.startDate}
                selectedEnd={selectedDates.endDate}
                onDatesChange={handleRentalDatesChange}
              />
            </div>
          </section>

          <SoundRental
            devices={soundDevices}
            addToCart={addToCart}
            removeFromCart={removeFromCart}
            incrementQuantity={incrementQuantity}
            decrementQuantity={decrementQuantity}
            cartItems={cartItems}
            selectedQuantities={selectedQuantities}
          />

          <LightRental
            devices={lightDevices}
            addToCart={addToCart}
            removeFromCart={removeFromCart}
            incrementQuantity={incrementQuantity}
            decrementQuantity={decrementQuantity}
            cartItems={cartItems}
            selectedQuantities={selectedQuantities}
          />

          <div id="otherrentals" className="mb-32">
            <OtherRentals
              devices={otherDevices}
              addToCart={addToCart}
              removeFromCart={removeFromCart}
              incrementQuantity={incrementQuantity}
              decrementQuantity={decrementQuantity}
              cartItems={cartItems}
              selectedQuantities={selectedQuantities}
            />
          </div>

          {/* HUOM! Poistettu vetoauton rekisterinumero -kenttä Rental.jsx:stä */}
        </div>
      </main>
    </>
  );
};

export default Rental;